import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Navigation } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';

const Contact = () => {
  const { settings } = useData();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    product: '',
    quantity: '',
    quantityUnit: 'kg',
    location: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);

    const detectLocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setIsDetectingLocation(true);
    try {
      const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });

      if (permissionStatus.state === 'denied') {
        toast.error('Location access denied. Please enable it in your browser settings.');
        setIsDetectingLocation(false);
        return;
      }

      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        });
      });

      const { latitude, longitude } = position.coords;
      
      // Use a reverse geocoding service to get address
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`
      );
      const data = await response.json();
      
      if (data.display_name) {
        setFormData(prev => ({
          ...prev,
          location: data.display_name
        }));
        toast.success('Location detected successfully!');
      } else {
        toast.error('Could not determine location name');
      }
    } catch (error) {
      console.error('Error detecting location:', error);
      if (typeof error === 'object' && error !== null && 'code' in error && (error as { code: number }).code === 1) {
        toast.error('Location access denied. Please enable location services.');
      } else {
        toast.error('Failed to detect location. Please enter manually.');
      }
    } finally {
      setIsDetectingLocation(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke('website-lead', {
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          mobile: formData.phone,
          subject: formData.product,
          message: `Company: ${formData.company}, Quantity: ${formData.quantity} ${formData.quantityUnit}, Location: ${formData.location}`
        }),
      });

      if (error) throw error;

      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        product: '',
        quantity: '',
        quantityUnit: 'kg',
        location: ''
      });
      
      toast.success('Your inquiry has been submitted successfully!');
      setTimeout(() => setIsSubmitted(false), 3000);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      
      let errorMessage = 'Failed to submit inquiry. Please try again.';
      
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Build contact info from settings
  const contactInfo = [
    {
      icon: Phone,
      title: 'Call Us',
      details: [settings.contact.phone],
      color: 'from-green-500 to-green-600'
    },
    {
      icon: Mail,
      title: 'Email Us',
      details: [settings.contact.email],
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      details: [settings.contact.address],
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      details: [settings.contact.businessHours],
      color: 'from-orange-500 to-orange-600'
    }
  ];

  return (
    <section id="contact" className="py-10 md:py-20 bg-gradient-to-br from-gray-50 to-amber-50">
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        <div className="text-center mb-10 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-amber-800 mb-4 md:mb-6">
            Get In Touch
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Ready to enhance your dairy business with premium feed solutions? 
            Contact us today for personalized quotes and expert guidance.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-12">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6 md:space-y-8">
            <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-lg">
              <h3 className="text-lg sm:text-2xl font-bold text-amber-800 mb-4 sm:mb-6">Contact Information</h3>
              
              {contactInfo.map((info, index) => (
                <div key={index} className="flex items-start space-x-4 mb-6 last:mb-0">
                  <div className={`inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r ${info.color} rounded-xl flex-shrink-0`}>
                    {/* Make icon clickable for phone and email */}
                    {info.title.toLowerCase().includes('call') && /^\+?\d[\d\- ]+$/.test(info.details[0]) ? (
                      <a href={`tel:${info.details[0].replace(/[^\d+]/g, '')}`} className="focus:outline-none" title={info.details[0]}>
                        <info.icon className="text-white" size={24} />
                      </a>
                    ) : info.title.toLowerCase().includes('email') && /@/.test(info.details[0]) ? (
                      <a href={`mailto:${info.details[0]}`} className="focus:outline-none" title={info.details[0]}>
                        <info.icon className="text-white" size={24} />
                      </a>
                    ) : (
                      <info.icon className="text-white" size={24} />
                    )}
                  </div>
                  <div>
                    <h4 className="font-bold text-amber-800 mb-1">{info.title}</h4>
                    {info.details.map((detail, idx) => {
                      // Make phone and email clickable
                      if (info.title.toLowerCase().includes('call') && /^\+?\d[\d\- ]+$/.test(detail)) {
                        return (
                          <a key={idx} href={`tel:${detail.replace(/[^\d+]/g, '')}`} className="text-gray-600 text-sm hover:underline block">{detail}</a>
                        );
                      } else if (info.title.toLowerCase().includes('email') && /@/.test(detail)) {
                        return (
                          <a key={idx} href={`mailto:${detail}`} className="text-gray-600 text-sm hover:underline block">{detail}</a>
                        );
                      } else {
                        return (
                          <p key={idx} className="text-gray-600 text-sm">{detail}</p>
                        );
                      }
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="bg-gradient-to-r from-amber-600 to-yellow-600 rounded-2xl p-4 sm:p-8 text-white">
              <h4 className="text-base sm:text-xl font-bold mb-2 sm:mb-4">Need Immediate Assistance?</h4>
              <p className="mb-4 sm:mb-6 opacity-90 text-sm sm:text-base">Call our experts for instant quotes and technical support.</p>
              <a
                href={`tel:${settings.contact.phone.replace(/[^\d+]/g, '')}`}
                className="block w-full bg-white text-amber-600 py-2 sm:py-3 px-4 sm:px-6 rounded-lg font-bold hover:bg-gray-100 transition-colors text-center text-sm sm:text-base"
              >
                Call Now: {settings.contact.phone}
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-lg">
              <h3 className="text-lg sm:text-2xl font-bold text-amber-800 mb-4 sm:mb-6">Send Us a Message</h3>
              
              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <CheckCircle className="text-green-500 mb-4" size={48} />
                  <h3 className="text-xl font-semibold mb-2 text-green-700">Thank you for your inquiry!</h3>
                  <p className="text-gray-700 mb-4 text-center max-w-xs">We have received your details and will get in touch soon.</p>
                  <div className="flex flex-col items-center space-y-2 mt-4">
                    <a href={`tel:${settings.contact.phone.replace(/[^\d+]/g, '')}`} className="text-amber-700 hover:underline flex items-center space-x-1">
                      <Phone size={18} className="inline-block mr-1" />
                      <span>{settings.contact.phone}</span>
                    </a>
                    <a href={`mailto:${settings.contact.email}`} className="text-amber-700 hover:underline flex items-center space-x-1">
                      <Mail size={18} className="inline-block mr-1" />
                      <span>{settings.contact.email}</span>
                    </a>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50 text-sm"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50 text-sm"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50 text-sm"
                        placeholder="Enter your phone number"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                        Company/Farm Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50 text-sm"
                        placeholder="Enter company name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <label htmlFor="product" className="block text-sm font-semibold text-gray-700 mb-2">
                        Product Interest
                      </label>
                      <select
                        id="product"
                        name="product"
                        value={formData.product}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50 text-sm"
                      >
                        <option value="">Select a product</option>
                        <option value="cotton-seed-cake">Cotton Seed Cake</option>
                        <option value="premium-khal">Premium Khal</option>
                        <option value="pellet-feed">Pellet Feed</option>
                        <option value="dairy-concentrate">Dairy Concentrate</option>
                        <option value="custom-formulation">Custom Formulation</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="quantity" className="block text-sm font-semibold text-gray-700 mb-2">
                        Quantity Required
                      </label>
                      <div className="flex space-x-2">
                        <input
                          type="number"
                          id="quantity"
                          name="quantity"
                          value={formData.quantity}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50 text-sm"
                          placeholder="Enter quantity"
                          min="1"
                        />
                        <select
                          name="quantityUnit"
                          value={formData.quantityUnit}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                          className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50 text-sm"
                        >
                          <option value="kg">KG</option>
                          <option value="bags">Bags</option>
                          <option value="tons">Tons</option>
                          <option value="quintals">Quintals</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2">
                      Location
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        id="location"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50 text-sm"
                        placeholder="Enter your location"
                      />
                      <button
                        type="button"
                        onClick={detectLocation}
                        disabled={isDetectingLocation || isSubmitting}
                        className="px-4 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                      >
                        <Navigation size={20} />
                        <span className="hidden sm:inline">Detect</span>
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-amber-600 text-white py-3 sm:py-4 px-6 sm:px-8 rounded-lg font-bold hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="mt-10 md:mt-16">
          <div className="bg-white rounded-2xl p-4 sm:p-8 shadow-lg">
            <h3 className="text-lg sm:text-2xl font-bold text-amber-800 mb-4 sm:mb-6 text-center">Find Us Here</h3>
            <div className="bg-gray-200 rounded-lg h-64 sm:h-96 flex items-center justify-center">
              <div className="text-center">
                <MapPin className="text-amber-600 mx-auto mb-2 sm:mb-4" size={32} />
                <p className="text-gray-600 font-medium text-sm sm:text-base">Interactive Map Coming Soon</p>
                <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2">
                  318/26, Bholaram Compound, Palda, Indore, MP - 452001
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;